import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Card, CardContent,
  Grid, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import AuthHeader from "../components/AuthHeader";

function PlayLogPage({ API_BASE }) {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const limit = 50;



  useEffect(() => {
    fetchUsers();
    fetchAllLogs(0); // 初回は全ユーザーのログを読み込み
  }, []);

  useEffect(() => {
    if (selectedUserId !== 'all') {
      fetchPlayLogs(0);
    } else {
      fetchAllLogs(0);
    }
  }, [selectedUserId]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/users`);
      if (response.ok) {
        const userData = await response.json();
        setUsers(userData);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchAllLogs = async (currentOffset = offset) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/all_play_logs?limit=${limit}&offset=${currentOffset}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch play logs');
      }
      
      const data = await response.json();
      
      if (currentOffset === 0) {
        setLogs(data.logs);
        setStats(data.stats);
      } else {
        setLogs(prev => [...prev, ...data.logs]);
      }
      
      setHasMore(data.pagination.has_more);
      setOffset(currentOffset);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayLogs = async (currentOffset = offset) => {
    setLoading(true);
    try {
      const endpoint = selectedUserId === 'all' 
        ? `${API_BASE}/all_play_logs?limit=${limit}&offset=${currentOffset}`
        : `${API_BASE}/user_play_logs/${selectedUserId}?limit=${limit}&offset=${currentOffset}`;
      
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error('Failed to fetch play logs');
      }
      
      const data = await response.json();
      
      if (currentOffset === 0) {
        setLogs(data.logs);
        setStats(data.stats);
      } else {
        setLogs(prev => [...prev, ...data.logs]);
      }
      
      setHasMore(data.pagination.has_more);
      setOffset(currentOffset);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    const nextOffset = offset + limit;
    if (selectedUserId === 'all') {
      fetchAllLogs(nextOffset);
    } else {
      fetchPlayLogs(nextOffset);
    }
    setOffset(nextOffset);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getContentTypeColor = (type) => {
    const colors = {
      word: 'primary',
      sentence: 'secondary', 
      skit: 'success',
      ex2: 'warning'
    };
    return colors[type] || 'default';
  };

  const getContentTypeLabel = (type) => {
    const labels = {
      word: '単語',
      sentence: '文章',
      skit: 'スキット',
      ex2: 'Ex2'
    };
    return labels[type] || type;
  };

  const handleBackupDownload = async () => {
    try {
      const response = await fetch(`${API_BASE}/user_db_backup`);

      if (!response.ok) {
        throw new Error('バックアップのダウンロードに失敗しました');
      }

      // Blobとしてダウンロード
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // ファイル名を生成（タイムスタンプ付き）
      const now = new Date();
      const timestamp = now.getFullYear() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') + '_' +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0');
      const filename = `user_db_backup_${timestamp}.sql`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(`エラー: ${err.message}`);
    }
  };


  if (loading && logs.length === 0) {
    return (
      <Container maxWidth="md" sx={{ paddingTop: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          読み込み中...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ paddingTop: 4 }}>
        <Typography variant="h5" color="error" align="center">
          エラー: {error}
        </Typography>
      </Container>
    );
  }



  return (
    <>
      <AuthHeader />
      <Container maxWidth="lg" sx={{ paddingTop: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          📊 再生ログ（管理ツール）
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackupDownload}
          sx={{ ml: 2 }}
        >
          💾 ユーザーDBバックアップ
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="user-select-label">ユーザー選択</InputLabel>
          <Select
            labelId="user-select-label"
            value={selectedUserId}
            label="ユーザー選択"
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <MenuItem value="all">全ユーザー</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name} ({user.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  総再生回数
                </Typography>
                <Typography variant="h5">
                  {stats.total_plays}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {selectedUserId === 'all' ? 'ユーザー数' : 'アクティブ日数'}
                </Typography>
                <Typography variant="h5">
                  {selectedUserId === 'all' ? stats.total_users : stats.active_days}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  総再生時間
                </Typography>
                <Typography variant="h5">
                  {formatDuration(stats.total_duration)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  種別内訳
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2">単語: {stats.word_plays}</Typography>
                  <Typography variant="body2">文章: {stats.sentence_plays}</Typography>
                  <Typography variant="body2">スキット: {stats.skit_plays}</Typography>
                  <Typography variant="body2">Ex2: {stats.ex2_plays}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日時</TableCell>
                {selectedUserId === 'all' && <TableCell>ユーザー</TableCell>}
                <TableCell>種別</TableCell>
                <TableCell>ファイル名</TableCell>
                <TableCell>再生時間</TableCell>
                <TableCell>再生速度</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {log.play_date}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {log.play_time}
                      </Typography>
                    </Box>
                  </TableCell>
                  {selectedUserId === 'all' && (
                    <TableCell>
                      <Typography variant="body2">
                        {log.user_name || `ID: ${log.user_id}`}
                      </Typography>
                      {log.user_email && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {log.user_email}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip 
                      label={getContentTypeLabel(log.content_type)}
                      color={getContentTypeColor(log.content_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.audio_file_name}
                    </Typography>
                    {log.is_video === 1 && (
                      <Chip label="動画" color="info" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDuration(log.duration_seconds)}
                  </TableCell>
                  <TableCell>
                    {log.playback_rate}x
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {hasMore && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'さらに読み込む'}
            </Button>
          </Box>
        )}
        
        {logs.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              再生ログがありません
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
    </>
  );
}

export default PlayLogPage;